"use client";

/**
 * Catering fulfillment chooser: pickup location or delivery address, then an
 * eligible date and time slot, all bound to the catering cart. Everything it
 * offers comes from the tenant's live catering configuration
 * (api.cateringSettings.*) for the request host's tenant; the server
 * re-validates the same promise at checkout and again at order creation.
 *
 * Public ordering surface: it follows the neutral "owner-style" ordering
 * vocabulary of the regular /order menu (white canvas, near-black ink,
 * hairline borders) so it renders correctly under every tenant theme,
 * including the Chef Roma dark funnel. The only tenant colour is the accent
 * token used for focus and selection rings (DESIGN.md §11).
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { CalendarDays, Check, Clock, MapPin, Pencil, Store, Truck } from "lucide-react";
import { api } from "@convex/_generated/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCateringCartStore, formatPrice } from "@/stores/cart-store";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { cn } from "@/lib/utils";

export type PublicCateringConfig = NonNullable<
  ReturnType<typeof useCateringPublicConfig>
>;

export function useCateringPublicConfig() {
  const { location } = useMarketingLocationContext();
  return useQuery(api.cateringSettings.getPublic, { locationId: location.id });
}

function useMinuteClock() {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 60000) * 60000);
  useEffect(() => {
    const id = setInterval(
      () => setNow(Math.floor(Date.now() / 60000) * 60000),
      30 * 1000,
    );
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Whether the catering cart carries a complete, checkout-ready promise. */
export function useCateringSelectionReady(config: PublicCateringConfig | undefined) {
  const cateringLocationId = useCateringCartStore((state) => state.cateringLocationId);
  const cateringLocationChosen = useCateringCartStore((state) => state.cateringLocationChosen);
  const scheduledFor = useCateringCartStore((state) => state.scheduledFor);
  const orderType = useCateringCartStore((state) => state.orderType);
  const deliveryAddress = useCateringCartStore((state) => state.deliveryAddress);
  const delivery = Boolean(config?.delivery.enabled) && orderType === "delivery";
  // A configured pickup location that is visible for browsing but not yet
  // orderable never counts as a checkout-ready selection (the server refuses
  // it again at plan, order-create and PaymentIntent time).
  const pickupOrderable =
    delivery ||
    config?.pickupLocations.some(
      (entry) => entry.locationId === cateringLocationId && entry.orderable,
    ) === true;
  return (
    Boolean(config?.enabled) &&
    cateringLocationId != null &&
    // Pickup needs an explicit choice; a stored auto-default or a kitchen
    // assigned by a delivery switch never counts.
    (delivery || cateringLocationChosen) &&
    scheduledFor != null &&
    pickupOrderable &&
    (!delivery || deliveryAddress != null)
  );
}

/**
 * The pickup location the panel treats as chosen: only a stored id that the
 * guest explicitly confirmed and that the tenant still offers. No fallback to
 * the host location or the first offered location; the guest must choose.
 */
export function resolvePickupChoice(input: {
  fulfillment: "pickup" | "delivery";
  offeredLocationIds: readonly string[];
  storedLocationId: string | null;
  chosen: boolean;
}): string | null {
  if (input.fulfillment !== "pickup") return null;
  if (!input.chosen || !input.storedLocationId) return null;
  return input.offeredLocationIds.includes(input.storedLocationId) ? input.storedLocationId : null;
}

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/35";
const FIELD = cn(
  "h-12 rounded-[10px] border-[#d6d6d6] bg-white px-3 text-base text-[#0d0d0d] shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] placeholder:text-[#767676]",
  "md:h-11 md:text-sm",
  FOCUS_RING,
);
const LABEL = "text-[13px] font-semibold text-[#0d0d0d]";

function chip(selected: boolean, disabled = false) {
  return cn(
    "inline-flex min-h-11 items-center justify-center rounded-[10px] border px-3 text-sm font-semibold transition-colors duration-150",
    FOCUS_RING,
    selected
      ? "border-[#0d0d0d] bg-[#0d0d0d] text-white"
      : "border-[#e3e3e3] bg-white text-[#0d0d0d] hover:border-[#0d0d0d]",
    disabled && "cursor-not-allowed opacity-45 hover:border-[#e3e3e3]",
  );
}

function formatScheduled(timestamp: number, timeZone: string) {
  return new Date(timestamp).toLocaleString("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function CateringFulfillmentPanel({
  config,
  className,
}: {
  config: PublicCateringConfig;
  className?: string;
}) {
  const { location } = useMarketingLocationContext();
  const now = useMinuteClock();

  const orderType = useCateringCartStore((state) => state.orderType);
  const setOrderType = useCateringCartStore((state) => state.setOrderType);
  const cateringLocationId = useCateringCartStore((state) => state.cateringLocationId);
  const cateringLocationChosen = useCateringCartStore((state) => state.cateringLocationChosen);
  const setCateringLocation = useCateringCartStore((state) => state.setCateringLocation);
  const scheduledFor = useCateringCartStore((state) => state.scheduledFor);
  const setScheduledFor = useCateringCartStore((state) => state.setScheduledFor);
  const deliveryAddress = useCateringCartStore((state) => state.deliveryAddress);
  const setDeliveryAddress = useCateringCartStore((state) => state.setDeliveryAddress);
  const setFlatDeliveryFee = useCateringCartStore((state) => state.setFlatDeliveryFee);
  const itemCount = useCateringCartStore((state) => state.getItemCount());

  const deliveryOffered = config.delivery.enabled;
  const fulfillment = deliveryOffered && orderType === "delivery" ? "delivery" : "pickup";

  // Pickup: the guest must explicitly pick a real platform location. A valid
  // prior explicit choice is preserved; nothing is ever auto-selected.
  const pickupChoice = useMemo(
    () =>
      resolvePickupChoice({
        fulfillment,
        offeredLocationIds: config.pickupLocations.map((entry) => entry.locationId),
        storedLocationId: cateringLocationId,
        chosen: cateringLocationChosen,
      }),
    [fulfillment, config.pickupLocations, cateringLocationId, cateringLocationChosen],
  );

  // Delivery assigns the tenant's kitchen as the fulfilling location (not a
  // guest choice, so it never confirms pickup). Pickup writes happen only in
  // the location card's onClick. The store empties the cart when the
  // location actually changes (catalog is scoped).
  const effectiveLocationId =
    fulfillment === "delivery" ? config.delivery.kitchenLocationId : pickupChoice;
  useEffect(() => {
    if (
      fulfillment === "delivery" &&
      config.delivery.kitchenLocationId &&
      config.delivery.kitchenLocationId !== cateringLocationId
    ) {
      setCateringLocation(config.delivery.kitchenLocationId);
    }
  }, [fulfillment, config.delivery.kitchenLocationId, cateringLocationId, setCateringLocation]);

  // Delivery fee is the tenant's flat fee, applied once an eligible address
  // is stored; pickup carries none.
  useEffect(() => {
    if (fulfillment === "delivery" && deliveryAddress) {
      setFlatDeliveryFee(config.delivery.flatFeeCents);
    } else {
      setFlatDeliveryFee(0);
    }
  }, [fulfillment, deliveryAddress, config.delivery.flatFeeCents, setFlatDeliveryFee]);

  // If delivery stops being offered, fall back to pickup.
  useEffect(() => {
    if (!deliveryOffered && orderType === "delivery") setOrderType("pickup");
  }, [deliveryOffered, orderType, setOrderType]);

  const dates = useQuery(
    api.cateringSettings.getDates,
    effectiveLocationId
      ? {
          locationId: location.id,
          fulfillment,
          fulfillmentLocationId: effectiveLocationId,
          now,
        }
      : "skip",
  );
  const [dateChoice, setDateChoice] = useState<string | null>(null);
  const selectedDate = useMemo(() => {
    if (!dates || dates.length === 0) return null;
    if (dateChoice && dates.some((entry) => entry.dateIso === dateChoice)) return dateChoice;
    if (scheduledFor != null) {
      const stored = new Date(scheduledFor).toLocaleDateString("en-CA", {
        timeZone: config.timeZone,
      });
      if (dates.some((entry) => entry.dateIso === stored)) return stored;
    }
    return dates[0]!.dateIso;
  }, [dates, dateChoice, scheduledFor, config.timeZone]);

  const slotsResult = useQuery(
    api.cateringSettings.getSlots,
    effectiveLocationId && selectedDate
      ? {
          locationId: location.id,
          fulfillment,
          fulfillmentLocationId: effectiveLocationId,
          dateIso: selectedDate,
          now,
        }
      : "skip",
  );
  const slots = slotsResult?.slots ?? null;

  // A stored time that is no longer offered (stale cart, advance window
  // passed, hours edited) is dropped so checkout can never carry it.
  useEffect(() => {
    if (!slots || scheduledFor == null) return;
    if (!slots.some((slot) => slot.timestamp === scheduledFor)) {
      setScheduledFor(null);
    }
  }, [slots, scheduledFor, setScheduledFor]);

  // Address draft (delivery).
  const [addr, setAddr] = useState({
    street: deliveryAddress?.street ?? "",
    apt: deliveryAddress?.apt ?? "",
    city: deliveryAddress?.city ?? "",
    state: deliveryAddress?.state ?? config.delivery.serviceAreaState ?? "",
    zip: deliveryAddress?.zip ?? "",
    instructions: deliveryAddress?.instructions ?? "",
  });
  const addressComplete =
    addr.street.trim().length > 0 &&
    addr.city.trim().length > 0 &&
    addr.state.trim().length === 2 &&
    addr.zip.replace(/\D/g, "").length === 5;
  const addressCheck = useQuery(
    api.cateringSettings.checkDeliveryAddress,
    fulfillment === "delivery" && addressComplete
      ? {
          locationId: location.id,
          address: {
            street: addr.street.trim(),
            apt: addr.apt.trim() || undefined,
            city: addr.city.trim(),
            state: addr.state.trim().toUpperCase(),
            zip: addr.zip.replace(/\D/g, "").slice(0, 5),
            instructions: addr.instructions.trim() || undefined,
          },
        }
      : "skip",
  );
  useEffect(() => {
    if (fulfillment !== "delivery") {
      if (deliveryAddress) setDeliveryAddress(null);
      return;
    }
    if (!addressComplete || addressCheck === undefined) return;
    if (addressCheck.eligible) {
      const next = {
        street: addr.street.trim(),
        apt: addr.apt.trim() || undefined,
        city: addr.city.trim(),
        state: addr.state.trim().toUpperCase(),
        zip: addr.zip.replace(/\D/g, "").slice(0, 5),
        instructions: addr.instructions.trim() || undefined,
      };
      if (JSON.stringify(next) !== JSON.stringify(deliveryAddress)) {
        setDeliveryAddress(next);
      }
    } else if (deliveryAddress) {
      setDeliveryAddress(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillment, addressComplete, addressCheck, addr]);

  const pickupLocation = config.pickupLocations.find(
    (entry) => entry.locationId === pickupChoice,
  );
  const pickupBrowseOnly =
    fulfillment === "pickup" && pickupLocation != null && !pickupLocation.orderable;

  // Progressive disclosure: once the promise is complete the panel collapses
  // to an editable summary. Anything incomplete or invalid keeps the full
  // form (and its inline status) visible.
  const ready =
    effectiveLocationId != null &&
    scheduledFor != null &&
    (fulfillment !== "delivery" || deliveryAddress != null);
  const [editing, setEditing] = useState(false);
  const showSummary = ready && !editing;

  const serviceAreaLabel = config.delivery.serviceAreaLocalities
    .map((entry) => entry.replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(", ");

  const addressStatus = !addressComplete
    ? `Enter a full address. We deliver catering within ${serviceAreaLabel}.`
    : addressCheck === undefined
      ? "Checking delivery area…"
      : addressCheck.eligible
        ? `Delivery from ${addressCheck.kitchenName ?? "our catering kitchen"} · ${formatPrice(addressCheck.feeCents)} flat fee, shown before payment.`
        : addressCheck.reason;
  const addressRejected = addressComplete && addressCheck !== undefined && !addressCheck.eligible;

  return (
    <section
      aria-label="Catering pickup or delivery"
      data-catering-plan-panel
      data-catering-plan-state={showSummary ? "summary" : "editing"}
      className={cn(
        "scroll-mt-40 rounded-[14px] border border-[#ededed] bg-white p-4 text-[#0d0d0d] shadow-[0_1px_2px_rgb(0_0_0_/_0.04)] sm:p-5 lg:scroll-mt-48",
        className,
      )}
    >
      {showSummary ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <p className="flex items-center gap-2 text-[15px] font-bold leading-5">
              {fulfillment === "delivery" ? (
                <Truck className="h-4 w-4 shrink-0 text-[#707070]" aria-hidden="true" />
              ) : (
                <Store className="h-4 w-4 shrink-0 text-[#707070]" aria-hidden="true" />
              )}
              <span className="truncate">
                {fulfillment === "delivery"
                  ? `Delivery · ${formatPrice(config.delivery.flatFeeCents)} flat`
                  : `Pickup · ${pickupLocation?.name ?? location.name}`}
              </span>
            </p>
            <p className="flex items-center gap-2 text-sm font-medium leading-5 text-[#575757]">
              <CalendarDays className="h-4 w-4 shrink-0 text-[#707070]" aria-hidden="true" />
              <span>{scheduledFor != null ? formatScheduled(scheduledFor, config.timeZone) : ""}</span>
            </p>
            {pickupBrowseOnly ? (
              <p
                role="status"
                data-catering-location-notice
                className="rounded-[10px] bg-[#fff6e5] px-3 py-2 text-[13px] font-medium leading-5 text-[#7a4b00]"
              >
                {pickupLocation?.unavailableReason}
              </p>
            ) : null}
            <p className="flex items-start gap-2 text-[13px] leading-5 text-[#707070]">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 break-words">
                {fulfillment === "delivery" && deliveryAddress
                  ? `${deliveryAddress.street}${deliveryAddress.apt ? `, ${deliveryAddress.apt}` : ""}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zip}`
                  : pickupLocation
                    ? `${pickupLocation.address.line1}, ${pickupLocation.address.city}, ${pickupLocation.address.state} ${pickupLocation.address.zip}`
                    : ""}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-[#d6d6d6] bg-white px-4 text-sm font-semibold text-[#0d0d0d] transition-colors hover:border-[#0d0d0d]",
              FOCUS_RING,
            )}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[17px] font-bold leading-6">Where and when</h2>
              <p className="mt-0.5 text-[13px] font-medium leading-5 text-[#707070]">
                Pick a spot, a day and a time. You can change it any time before checkout.
              </p>
            </div>
            {ready ? (
              <button
                type="button"
                onClick={() => setEditing(false)}
                className={cn(
                  "inline-flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-semibold text-[#0d0d0d] hover:bg-[#f4f4f5]",
                  FOCUS_RING,
                )}
              >
                Done
              </button>
            ) : null}
          </div>

          <div
            className="mt-4 grid grid-cols-2 gap-1 rounded-full bg-[#ebebec] p-1"
            role="radiogroup"
            aria-label="Fulfillment"
          >
            <button
              type="button"
              role="radio"
              aria-checked={fulfillment === "pickup"}
              onClick={() => setOrderType("pickup")}
              className={cn(
                "flex h-11 items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors",
                FOCUS_RING,
                fulfillment === "pickup"
                  ? "bg-white text-[#0d0d0d] shadow-[0_1px_3px_rgb(0_0_0_/_0.16)]"
                  : "text-[#444] hover:text-[#0d0d0d]",
              )}
            >
              <Store className="h-4 w-4" aria-hidden="true" /> Pickup
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={fulfillment === "delivery"}
              disabled={!deliveryOffered}
              onClick={() => setOrderType("delivery")}
              className={cn(
                "flex h-11 items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                FOCUS_RING,
                fulfillment === "delivery"
                  ? "bg-white text-[#0d0d0d] shadow-[0_1px_3px_rgb(0_0_0_/_0.16)]"
                  : "text-[#444] hover:text-[#0d0d0d]",
              )}
            >
              <Truck className="h-4 w-4" aria-hidden="true" />
              {deliveryOffered ? (
                <>
                  Delivery
                  <span className="hidden xs:inline"> · {formatPrice(config.delivery.flatFeeCents)}</span>
                </>
              ) : (
                "Delivery unavailable"
              )}
            </button>
          </div>

          {fulfillment === "pickup" ? (
            <fieldset className="mt-4 min-w-0">
              <legend className={cn(LABEL, "flex w-full items-baseline justify-between gap-3")}>
                <span>Pickup location</span>
                {pickupChoice == null ? (
                  <span data-catering-pickup-empty className="text-[12px] font-medium text-[#707070]">
                    Choose a pickup location
                  </span>
                ) : null}
              </legend>
              <div className="mt-2 grid gap-2" role="radiogroup" aria-label="Pickup location">
                {config.pickupLocations.map((entry) => {
                  const selected = entry.locationId === pickupChoice;
                  return (
                    <button
                      key={entry.locationId}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setCateringLocation(entry.locationId, { chosen: true })}
                      className={cn(
                        "flex min-h-11 w-full items-start gap-3 rounded-[10px] border px-3 py-2.5 text-left transition-colors",
                        FOCUS_RING,
                        selected
                          ? "border-[#0d0d0d] bg-white ring-1 ring-[#0d0d0d]"
                          : "border-[#e3e3e3] bg-white hover:border-[#0d0d0d]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                          selected ? "border-[#0d0d0d] bg-[#0d0d0d] text-white" : "border-[#c8c8c8]",
                        )}
                        aria-hidden="true"
                      >
                        {selected ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold leading-5">
                          {entry.name}
                          {!entry.orderable ? (
                            <span className="rounded-full bg-[#fff6e5] px-2 py-0.5 text-[11px] font-semibold text-[#7a4b00]">
                              Browse only
                            </span>
                          ) : null}
                        </span>
                        <span className="block text-[13px] leading-5 text-[#707070]">
                          {entry.address.line1}
                          {entry.address.line2 ? `, ${entry.address.line2}` : ""}, {entry.address.city}
                          {entry.phone ? ` · ${entry.phone}` : ""}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {pickupBrowseOnly ? (
                <p
                  role="status"
                  data-catering-location-notice
                  className="mt-2 rounded-[10px] bg-[#fff6e5] px-3 py-2 text-[13px] font-medium leading-5 text-[#7a4b00]"
                >
                  {pickupLocation?.unavailableReason} You can still browse this location&apos;s menu and times.
                </p>
              ) : null}
            </fieldset>
          ) : (
            <div className="mt-4 grid grid-cols-6 gap-3">
              <div className="col-span-6 space-y-1.5">
                <Label htmlFor="catering-street" className={LABEL}>Street address</Label>
                <Input id="catering-street" className={FIELD} autoComplete="address-line1" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="catering-apt" className={LABEL}>Suite</Label>
                <Input id="catering-apt" className={FIELD} autoComplete="address-line2" value={addr.apt} onChange={(e) => setAddr({ ...addr, apt: e.target.value })} />
              </div>
              <div className="col-span-4 space-y-1.5">
                <Label htmlFor="catering-city" className={LABEL}>City</Label>
                <Input id="catering-city" className={FIELD} autoComplete="address-level2" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="catering-state" className={LABEL}>State</Label>
                <Input id="catering-state" className={FIELD} maxLength={2} autoComplete="address-level1" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase() })} />
              </div>
              <div className="col-span-4 space-y-1.5">
                <Label htmlFor="catering-zip" className={LABEL}>ZIP</Label>
                <Input id="catering-zip" className={FIELD} inputMode="numeric" autoComplete="postal-code" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value.replace(/\D/g, "").slice(0, 5) })} />
              </div>
              <div className="col-span-6 space-y-1.5">
                <Label htmlFor="catering-instructions" className={LABEL}>Delivery notes (optional)</Label>
                <Input id="catering-instructions" className={FIELD} placeholder="Gate code, entrance, setup spot" value={addr.instructions} onChange={(e) => setAddr({ ...addr, instructions: e.target.value })} />
              </div>
              <p
                role="status"
                data-catering-address-status
                className={cn(
                  "col-span-6 rounded-[10px] px-3 py-2 text-[13px] font-medium leading-5",
                  addressRejected
                    ? "bg-[#fdf2f0] text-[#9f2b1c]"
                    : "bg-[#f4f4f5] text-[#575757]",
                )}
              >
                {addressStatus}
              </p>
            </div>
          )}

          {effectiveLocationId == null ? (
            <p data-catering-schedule-pending className="mt-5 rounded-[10px] bg-[#f4f4f5] px-3 py-2 text-[13px] font-medium leading-5 text-[#575757]">
              Dates and times appear once you choose a pickup location.
            </p>
          ) : (
          <>
          <div className="mt-5 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p id="catering-date-label" className={cn(LABEL, "flex items-center gap-1.5")}>
                <CalendarDays className="h-4 w-4 text-[#707070]" aria-hidden="true" /> Date
              </p>
              {dates === undefined ? (
                <span className="text-[13px] text-[#707070]">Loading dates…</span>
              ) : null}
            </div>
            {dates && dates.length === 0 ? (
              <p className="mt-2 rounded-[10px] bg-[#f4f4f5] px-3 py-2 text-[13px] font-medium text-[#575757]">
                No dates are open right now. Call the restaurant to plan an order.
              </p>
            ) : (
              <div
                id="catering-date"
                role="radiogroup"
                aria-labelledby="catering-date-label"
                className="-mx-4 mt-2 flex snap-x gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide sm:-mx-5 sm:px-5"
              >
                {(dates ?? []).map((entry) => {
                  const selected = entry.dateIso === selectedDate;
                  const [weekday, ...rest] = entry.label.split(", ");
                  return (
                    <button
                      key={entry.dateIso}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        setDateChoice(entry.dateIso);
                        setScheduledFor(null);
                      }}
                      className={cn(chip(selected), "w-[4.75rem] shrink-0 snap-start flex-col gap-0 px-2 py-1.5 leading-tight")}
                    >
                      <span className="text-[11px] font-medium uppercase tracking-[0.08em] opacity-80">
                        {weekday}
                      </span>
                      <span className="text-sm font-bold">{rest.join(", ") || entry.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 min-w-0">
            <p id="catering-time-label" className={cn(LABEL, "flex items-center gap-1.5")}>
              <Clock className="h-4 w-4 text-[#707070]" aria-hidden="true" />
              {fulfillment === "delivery" ? config.deliveryTimeLabel : config.pickupTimeLabel}
            </p>
            {slots === null ? (
              <p className="mt-2 text-[13px] font-medium text-[#707070]">
                {selectedDate ? "Loading times…" : "Choose a date first."}
              </p>
            ) : slots.length === 0 ? (
              <p className="mt-2 rounded-[10px] bg-[#f4f4f5] px-3 py-2 text-[13px] font-medium text-[#575757]">
                {slotsResult?.reason ?? "No times are open on this date."}
              </p>
            ) : (
              <div
                id="catering-time"
                role="radiogroup"
                aria-labelledby="catering-time-label"
                className="mt-2 grid grid-cols-3 gap-2 xs:grid-cols-4 sm:grid-cols-4"
              >
                {slots.map((slot) => {
                  const selected = slot.timestamp === scheduledFor;
                  return (
                    <button
                      key={slot.timestamp}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        setScheduledFor(slot.timestamp);
                        setEditing(false);
                      }}
                      className={cn(chip(selected), "px-1 tabular-nums")}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          </>
          )}

          {itemCount > 0 && effectiveLocationId ? (
            <p className="mt-4 text-[12px] leading-5 text-[#707070]">
              Menu and prices are for the selected location. Changing location clears your catering cart.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
